import { Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface BlogPaginationProps {
  page: number;
  pageCount: number;
  /** Route template for the current listing, e.g. "/blog" or "/blog/$tool". */
  to: string;
  /** Route params for `to` (e.g. { tool, topic }). */
  params?: Record<string, string>;
}

/**
 * Page numbers to render, collapsing long ranges to first … current±1 … last.
 * e.g. page 6 of 10 → [1, "…", 5, 6, 7, "…", 10].
 */
function pageWindow(
  page: number,
  pageCount: number,
): Array<number | "ellipsis"> {
  const out: Array<number | "ellipsis"> = [];
  for (let p = 1; p <= pageCount; p++) {
    if (p === 1 || p === pageCount || (p >= page - 1 && p <= page + 1)) {
      out.push(p);
    } else if (out[out.length - 1] !== "ellipsis") {
      out.push("ellipsis");
    }
  }
  // An ellipsis that hides exactly one page wastes the same space — show it.
  return out.map((v, i) => {
    if (v !== "ellipsis") return v;
    const prev = out[i - 1];
    const next = out[i + 1];
    return typeof prev === "number" &&
      typeof next === "number" &&
      next - prev === 2
      ? prev + 1
      : v;
  });
}

const base =
  "inline-flex h-10 min-w-10 items-center justify-center rounded-lg border px-3 text-sm font-medium tabular-nums transition-all";
const idle =
  "border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground dark:border-white/[0.12] dark:bg-[oklch(0.255_0.022_265)]";
const active =
  "border-primary/60 bg-primary/15 text-primary shadow-[0_0_0_1px_oklch(0.5_0.22_260/0.25)_inset]";
const disabled =
  "border-border/60 bg-muted text-muted-foreground/40 dark:border-white/[0.06] dark:bg-[oklch(0.2_0.02_265)]";

/** URL-based pager: page 1 has no `?page` param so the canonical URL stays clean. */
const pageSearch = (p: number) => ({ page: p > 1 ? p : undefined });

export function BlogPagination({
  page,
  pageCount,
  to,
  params,
}: BlogPaginationProps) {
  if (pageCount <= 1) return null;
  const pages = pageWindow(page, pageCount);

  return (
    <nav
      aria-label="Pagination"
      className="mt-12 flex flex-wrap items-center justify-center gap-1.5"
    >
      {page > 1 ? (
        <Link
          to={to}
          params={params}
          search={pageSearch(page - 1)}
          className={`${base} ${idle}`}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Link>
      ) : (
        <span className={`${base} ${disabled}`} aria-hidden>
          <ChevronLeft className="h-4 w-4" />
        </span>
      )}

      {pages.map((p, i) =>
        p === "ellipsis" ? (
          <span
            key={`ellipsis-${i}`}
            className="px-1 text-sm text-muted-foreground/50"
            aria-hidden
          >
            …
          </span>
        ) : (
          <Link
            key={p}
            to={to}
            params={params}
            search={pageSearch(p)}
            aria-label={`Page ${p}`}
            aria-current={p === page ? "page" : undefined}
            className={`${base} ${p === page ? active : idle}`}
          >
            {p}
          </Link>
        ),
      )}

      {page < pageCount ? (
        <Link
          to={to}
          params={params}
          search={pageSearch(page + 1)}
          className={`${base} ${idle}`}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Link>
      ) : (
        <span className={`${base} ${disabled}`} aria-hidden>
          <ChevronRight className="h-4 w-4" />
        </span>
      )}
    </nav>
  );
}
